// @flow
import ReactDOM from 'react-dom'
import { customAlphabet } from 'nanoid'
import { SERVICES } from 'core/constants/services'
import { initLogger } from 'utils/logger'
import { HistoryService } from 'services/history/service'
import { Updater } from 'services/updater'
import { DateService } from 'services/date'
import { ioc } from 'core/ioc'
// Init session BEFORE run any app
import { SessionService } from 'services/session'
import type { CreateConfig, Widget } from './types'
import { Root } from './root'
import { setMounted, setWidgets, updateWidget } from './model/events'
import { mounted, $widgets } from './model/store'

const customNanoid = customAlphabet('1234567890abcdef-', 22)

class FoxfordWidgets {
  domId: string | void

  domNode: HTMLDivElement | void

  mounted: boolean = false

  widgets: $PropertyType<CreateConfig, 'widgets'> = []

  constructor() {
    this.init()

    mounted.watch((value) => {
      this.mounted = value
    })

    $widgets.watch((value) => {
      this.widgets = value
    })
  }

  init = () => {
    initLogger()
    ioc.service(SERVICES.HISTORY, HistoryService)
    ioc.service(SERVICES.UPDATER, Updater)
    SessionService.initialize()

    DateService.initLocale('ru')
    DateService.setTimezone('Europe/Moscow')
  }

  _createDomNode = () => {
    this.domId = customNanoid()
    const rootNode = document.createElement('div')

    rootNode.setAttribute('id', this.domId)

    if (document.body) {
      document.body.appendChild(rootNode)
    }

    this.domNode = rootNode

    return rootNode
  }

  _loadColors = () => {
    // eslint-disable-next-line no-unused-expressions
    import(/* webpackMode: "eager" */ './assets/root.sass')
  }

  _loadFonts = () => {
    // eslint-disable-next-line no-unused-expressions
    import(/* webpackMode: "eager" */ './assets/fonts.sass')

    this.widgets.forEach((widget) => {
      const keys = Object.keys(widget).filter((key) => {
        return widget[key] instanceof HTMLElement
      })

      keys.forEach((key) => {
        // flowlint-next-line unclear-type:off
        const element: HTMLElement = ((widget[key]: any): HTMLElement)

        element.style.fontFamily = 'Circe, -apple-system, system-ui, BlinkMacSystemFont, sans-serif'
      })
    })
  }

  create(options: CreateConfig) {
    if (this.mounted) return

    this._setWidgets(options.widgets)

    try {
      const domNode = this._createDomNode()

      if (options.rootColors === true) this._loadColors()
      if (options.loadFonts === true) this._loadFonts()

      ReactDOM.render(<Root analyticContext={options.analyticContext} />, domNode)
    } catch (error) {
      console.error(error) // eslint-disable-line
    }
  }

  _setWidgets = (widgets: $PropertyType<CreateConfig, 'widgets'>) => {
    setWidgets(widgets)
  }

  update = (name: $PropertyType<Widget<{ ... }>, 'name'>, props: Widget<{ ... }>) => {
    updateWidget({ name, props })
  }

  destroy() {
    setWidgets([])
    setMounted(false)
    ReactDOM.unmountComponentAtNode(this.domNode)
    if (this.domId !== undefined) {
      document.getElementById(this.domId)?.remove()
      this.domNode = undefined
      this.domId = undefined
    }
  }
}

const widgets = new FoxfordWidgets()

window.FoxfordWidgets = widgets

export { widgets as Widgets }
